import { useQuery } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { useAuth } from "./useAuth";
import { log } from "@/modules/shared/lib/observability";

export interface CurrentTeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  avatar_url: string | null;
  commission_percent: number;
}

export function useCurrentTeamMember() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["current-team-member", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await db
        .from("professionals")
        .select("id, name, email, role, status, avatar_url, commission_percent")
        .eq("user_id", user.id)
        .eq("professional_type", "operacional")
        .maybeSingle();

      if (error) {
        log.error("Error fetching current team member", {}, error);
        return null;
      }

      return data as CurrentTeamMember;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

// Helper to check if user should see all data (admin) or filtered data
export function useDataAccessLevel() {
  const { data: teamMember, isLoading, isError } = useCurrentTeamMember();

  const normalizedRole = (teamMember?.role || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");

  const isAdmin = normalizedRole === "admin" || normalizedRole === "administrador";
  const isAccountManager = normalizedRole === "account_manager";
  const isBusinessPartner = normalizedRole === "business_partner";
  const isAgileMaster = normalizedRole === "agile_master";

  // If no team member found (error or null), treat as full access (admin-like)
  const noTeamMember = !isLoading && (isError || teamMember === null);

  return {
    teamMember,
    isLoading,
    isAdmin,
    isAccountManager,
    isBusinessPartner,
    isAgileMaster,
    // Admin, Agile Master, or users without team_member record see all data
    hasFullAccess: isAdmin || isAgileMaster || noTeamMember,
    // Team member ID for filtering (null if no team member)
    teamMemberId: teamMember?.id || null,
    // Flag to indicate if team member data is ready (loaded or determined as not existing)
    isReady: !isLoading,
  };
}
